import { Injectable } from '@angular/core';

import {
  READING_INTENTIONS,
  ReadingIntention,
  ReadingIntentionsKey,
} from '®shared/annotations';
import { OnlineResource } from '®shared/bibliographic-resource';
import { NO_IMPORTANCE_SET } from '®shared/list-item/importance/importance.constant';
import { EditableItem } from '®shared/reading-list/models';
import { Importance } from '®shared/tenant';

import { CourseReserveStatusFilterAndLabel } from './course-reserve-status-filter-and-label';
import { DigitisationStatusFilterAndLabel } from './digitisation-status-filter-and-label';
import { QuickFilter } from './quick-filter';
import {
  getCourseReserveStatusFilterForItem,
  getDigitisationStatusFilterForItem,
} from './quick-filters-utils';
import { ResourceTypeFilter } from './resource-type-filter';
import { StudyNoteFilter } from './study-note-filter';

@Injectable()
export class QuickFiltersService {
  /**
   * Constructs a quick-filter object for filtering items by importance.
   *
   * @param {Importance} importance - an importance
   * @return {QuickFilter} - a quick-filter specification for filtering by importance
   */
  quickFilterForImportance(importance: Importance): QuickFilter {
    return {
      id: importance.id,
      type: 'importance',
      value: importance.description,
      predicate: (item: EditableItem) =>
        item && item.importance && item.importance.id === importance.id,
    };
  }

  quickFilterForNoImportanceSet(): QuickFilter {
    return {
      id: NO_IMPORTANCE_SET,
      type: 'importance',
      value: $localize`:@@quickFilter-label-noImportanceSet:Importance not set`,
      predicate: (item: EditableItem) =>
        item &&
        !item.importance &&
        Boolean(item.resource) &&
        item.resource.isNonPageResource(),
    };
  }

  /**
   * Constructs a quick-filter object for filtering items by reading intention.
   *
   * @param {ReadingIntention} intention - an intention specification
   * @param {(EditableItem) => ReadingIntentionsKey} getReadingIntentionForItem - for getting an item's reading intention
   * @return {QuickFilter} - a quick-filter specification for filtering by intention
   */
  quickFilterForIntention(
    intention: ReadingIntention,
    getReadingIntentionForItem: (item: EditableItem) => ReadingIntentionsKey,
  ): QuickFilter {
    return {
      id: intention.value,
      type: 'readingIntention',
      value: intention.label,
      predicate: (item: EditableItem) => {
        const itemIntention = getReadingIntentionForItem(item);
        if (intention === READING_INTENTIONS.Undecided) {
          return !itemIntention;
        }

        return itemIntention && itemIntention === intention.value;
      },
    };
  }

  /**
   * Constructs a quick-filter object for filtering items by presence or absence of a study note.
   *
   * @param {StudyNoteFilter} studyNoteFilter - specifies whether to filter to presence/absence of study note
   * @param {(EditableItem) => string} getStudyNoteForItem - for getting an item's study note
   * @return {QuickFilter} - a quick-filter specification for filtering by study note
   */
  quickFilterForStudyNotePresent(
    studyNoteFilter: StudyNoteFilter,
    getStudyNoteForItem: (item: EditableItem) => string | undefined,
  ): QuickFilter {
    return {
      id: studyNoteFilter.isPresent,
      type: 'studyNotes',
      value: studyNoteFilter.label,

      predicate: (item: EditableItem) => {
        const hasStudyNote = getStudyNoteForItem(item) ? true : false;
        return hasStudyNote === studyNoteFilter.isPresent;
      },
    };
  }

  /**
   * Creates a quick-filter object to allow filtering by whether a resource has
   * an attached online resource.
   *
   * @param {ResourceTypeFilter} resourceTypeFilter - a value providing fields describing the presence of an online resource.
   * @param {(EditableItem) => OnlineResource} getOnlineResourceForItem - fetches online resource for an item
   * @return {QuickFilter} - a quick-filter specification for filtering by the presence of an associated online-resource.
   */
  quickFilterForResourceType(
    resourceTypeFilter: ResourceTypeFilter,
    getOnlineResourceForItem: (
      item: EditableItem,
    ) => OnlineResource | undefined,
  ): QuickFilter {
    return {
      id: resourceTypeFilter.isOnlineResource,
      type: 'resourceType',
      value: resourceTypeFilter.label,

      predicate: (item: EditableItem) => {
        const isOnlineResource = getOnlineResourceForItem(item) ? true : false;
        return isOnlineResource === resourceTypeFilter.isOnlineResource;
      },
    };
  }

  quickFilterForLibraryNote(): QuickFilter {
    return {
      id: 'hasLibraryNote',
      type: 'hasLibraryNote',
      value: true,
      predicate: (item: EditableItem) => Boolean(item.library_note),
    };
  }

  /**
   * Creates a quick-filter object to allow filtering by whether an item has
   * digitised content with a status that matches the filter predicate.
   *
   * @param {DigitisationStatusFilterAndLabel} digitisationStatusFilter - a value providing the desired digitisation status.
   * @return {QuickFilter} - a quick-filter specification for filtering by the status of digitised content on an item.
   */
  quickFilterForDigitisationStatus(
    digitisationStatusFilter: DigitisationStatusFilterAndLabel,
  ): QuickFilter {
    return {
      id: digitisationStatusFilter.value,
      type: 'digitisationStatus',
      value: digitisationStatusFilter.label,
      predicate: (item: EditableItem) => {
        return (
          getDigitisationStatusFilterForItem(item) ===
          digitisationStatusFilter.value
        );
      },
    };
  }

  /**
   * Creates a quick-filter object to allow filtering by whether an item has
   * course reserve content with a status that matches the filter predicate.
   *
   * @param {CourseReserveStatusFilterAndLabel} courseReserveStatusFilter - a value providing the desired course reserve status.
   * @return {QuickFilter} - a quick-filter specification for filtering by the status of course reserve content on an item.
   */
  quickFilterForCourseReserveStatus(
    courseReserveStatusFilter: CourseReserveStatusFilterAndLabel,
  ): QuickFilter {
    return {
      id: courseReserveStatusFilter.value,
      type: 'courseReserveStatus',
      value: courseReserveStatusFilter.label,
      predicate: (item: EditableItem) => {
        return (
          getCourseReserveStatusFilterForItem(item) ===
          courseReserveStatusFilter.value
        );
      },
    };
  }
}
